import { Injectable } from '@angular/core';
import { Profile } from '@icc/window-door/types';
import { ConfigurationsService } from '@icc/configurator/shared';
import { core } from '@icc/shared/helpers';

@Injectable()
export class ProfilesUpdateService {
    static isVariableWidthProfile(profile?: Profile | null): boolean {
        if (!profile) {
            return false;
        }

        return !!profile.widthOutType
            || !!profile.widthOutRanges?.length
            || (
                profile.widthOutMin != null
                && profile.widthOutMax != null
                && profile.widthOutMin !== profile.widthOutMax
            );
    }

    static nextNegativeId(
        usedProfiles: Array<{ id?: number | string }> = [],
        clonedProfiles: Array<{ id?: number | string }> = [],
    ): number {
        const usedProfilesIds = usedProfiles
            .map(p => Number(p?.id))
            .filter(n => Number.isFinite(n));
        const clonedProfilesIds = clonedProfiles
            .map(p => Number(p?.id))
            .filter(n => Number.isFinite(n));

        const allNumericIds = [...usedProfilesIds, ...clonedProfilesIds];
        const minId = allNumericIds.length ? Math.min(...allNumericIds, 0) : 0;
        return minId <= 0 ? minId - 1 : -1;
    }

    static cloneVariableWidthProfile(
        profile: Profile,
        usedProfiles: Array<{ id?: number | string }> = [],
        clonedProfiles: Array<{ id?: number | string }> = [],
    ): Profile {
        const cloned = core.copy(profile) as Profile & { _clonedFrom?: number; id?: number };
        cloned._clonedFrom = profile.id;
        cloned.id = ProfilesUpdateService.nextNegativeId(usedProfiles, clonedProfiles);
        clonedProfiles.push(cloned);
        return cloned as Profile;
    }

    static updateExistingMullionClone(existingClone: Profile, sourceProfile: Profile): Profile {
        const updatedClone = core.copy(sourceProfile) as Profile & {
            _clonedFrom?: number;
            selectedWidth?: number;
            widthOut?: number;
            originalProfile?: number;
        };
        const existingCloneId = Number(existingClone?.id);
        const sourceProfileId = Number(sourceProfile?._clonedFrom ?? sourceProfile.id);
        updatedClone.id = Number.isFinite(existingCloneId) ? existingCloneId : existingClone?.id;
        updatedClone._clonedFrom = sourceProfileId;
        updatedClone.originalProfile = sourceProfileId;
        if (sourceProfile.selectedWidth !== null && sourceProfile.selectedWidth !== undefined) {
            updatedClone.selectedWidth = sourceProfile.selectedWidth;
            updatedClone.widthOut = sourceProfile.selectedWidth;
        }
        if (sourceProfile.width !== null && sourceProfile.width !== undefined) {
            updatedClone.width = sourceProfile.width;
        }
        return updatedClone as Profile;
    }

    constructor(
        protected configurationsService: ConfigurationsService<'door'>,
    ) {}

    updateProfileValues(profile: Profile, delta: number, coupling?: any, clampedWidth?: number, pageData?: any) {
            if (!profile || !profile.widthOutType) {
                return;
            }
            const profileId = Number(profile?.id ?? (pageData?.selectedProfile?.id));
            const deltaValue = Number.isFinite(delta) ? delta : 0;
            const halfDelta = deltaValue / 2;
            if (profile.spaceGlass !== null && profile.spaceGlass !== undefined) {
                profile.spaceGlass = Number(profile.spaceGlass) + halfDelta;
            }
            if (profile.spaceSash !== null && profile.spaceSash !== undefined) {
                profile.spaceSash = Number(profile.spaceSash) + halfDelta;
            }
            if (profile.spaceMullion !== null && profile.spaceMullion !== undefined) {
                profile.spaceMullion = Number(profile.spaceMullion) + halfDelta;
            }
            if (profile.spaceAlignment !== null && profile.spaceAlignment !== undefined) {
                profile.spaceAlignment = Number(profile.spaceAlignment) + halfDelta;
            }
    
            if (pageData?.selectedProfile?.width && pageData.selectedProfile.width !== undefined && pageData.selectedProfile.width !== null) {
                profile.width = pageData.selectedProfile.width + deltaValue;
            }
            else{
                profile.width = profile.width + deltaValue;
            }
            const newInnerWidth = profile.width;
            const conf = this.configurationsService.conf.Current;
            const selectedWidth = clampedWidth ?? profile?.selectedWidth ?? profile.widthOut;
    
            if (pageData?.selectedProfile && Number(pageData.selectedProfile.id) === profileId) {
                pageData.selectedProfile.width = newInnerWidth;
            }
            if (conf && Array.isArray(conf.couplings)) {
                if (profile.type === 'coupling' && typeof profile.id !== 'undefined') {
                    conf.couplings.forEach((coupling: any) => {
                        if (Number(coupling.profileId) === Number(profile.id)) {
                            coupling.width = newInnerWidth;
                            if (selectedWidth !== undefined) {
                                coupling.widthOut = selectedWidth;
                                coupling.outerWidth = selectedWidth;
                                coupling.selectedWidth = selectedWidth;
                            }
                        }
                    });
                } else {
                    conf.couplings.forEach((coupling: any) => {
                        if (Number(coupling.profileId) === profileId) {
                            coupling.width = newInnerWidth;
                            if (selectedWidth !== undefined) {
                                coupling.widthOut = selectedWidth;
                                coupling.outerWidth = selectedWidth;
                                coupling.selectedWidth = selectedWidth;
                            }
                        }
                    });
                }
            }
    
            if (conf && Array.isArray(conf.UsedProfiles) && (pageData.mullionId || profileId < 0)) {
                const usedProfile = conf.UsedProfiles.find((p: any) => Number(p.id) === profileId);
                const isCouplingBaseProfile = profileId > 0;
                if (usedProfile && !isCouplingBaseProfile) {
                    usedProfile.width = newInnerWidth;
                    if (selectedWidth !== undefined) {
                        usedProfile.widthOut = selectedWidth;
                        usedProfile.selectedWidth = selectedWidth;
                    }
                    }
            }
            if (pageData?.profiles && Array.isArray(pageData.profiles) && !pageData.mullionId) {
                const pageProfile = pageData.profiles.find((p: any) => Number(p.id) === profileId);
                const isCouplingBaseProfile = profile.type === 'coupling' && profileId > 0;
                if (pageProfile && !isCouplingBaseProfile) {
                    pageProfile.width = newInnerWidth;
                    pageData.selectedProfile.widthOut = selectedWidth;
                    pageData.selectedProfile.selectedWidth = selectedWidth;
                }
            }
    
            
    
            if (conf && Array.isArray(conf.Mullions)) {
                conf.Mullions.forEach((mullion: any) => {
                    const matchesMullion = !pageData.mullionId
                        || Number(mullion.id) === Number(pageData.mullionId);
                    if (Number(mullion.profileId) === profileId && matchesMullion) {
                        mullion.width = newInnerWidth;
                        if (selectedWidth !== undefined) {
                            mullion.widthOut = selectedWidth;
                            mullion.outerWidth = selectedWidth;
                            mullion.selectedWidth = selectedWidth;
                        }
                    }
                });
            }
    
            if (conf && Array.isArray(conf.Sashes)) {
                const sashes = conf.Sashes;
                sashes.forEach((sash: any) => {
                    if (Array.isArray(sash.intMullions)) {
                        sash.intMullions.forEach((intMullion: any) => {
                            const mullionProfile = conf.UsedProfiles?.find((p: any) => Number(p.id) === Number(intMullion.profileId));
                            const effectiveProfileId = mullionProfile?._clonedFrom ?? intMullion.profileId;
                            const matchesProfile = Number(effectiveProfileId) === profileId
                                || Number(intMullion.profileId) === profileId;
                            const matchesMullion = !pageData.mullionId
                                || Number(intMullion.id) === Number(pageData.mullionId);
                            if (matchesProfile && matchesMullion) {
                                if (selectedWidth !== undefined) {
                                    intMullion.selectedWidth = selectedWidth;
                                    intMullion.widthOut = selectedWidth;
                                }
                                intMullion.width = newInnerWidth;
                                intMullion.widthDeltaInner = deltaValue;
                                const isVertical = intMullion.direction === 'vertical';
                                if (isVertical) {
                                    intMullion.rWidth = selectedWidth ?? intMullion.rWidth;
                                } else {
                                    intMullion.rHeight = selectedWidth ?? intMullion.rHeight;
                                }
            
                                if (mullionProfile && (!pageData.mullionId || Number(mullionProfile.id) < 0)) {
                                    mullionProfile.width = newInnerWidth;
                                    if (selectedWidth !== undefined) {
                                        mullionProfile.widthOut = selectedWidth;
                                        mullionProfile.selectedWidth = selectedWidth;
                                    }
                                }
                            }
                        });
                    }
                });
            }
        }
}
